// Shared render helpers for agent chat messages (KaraWidget + DualChatPanel).
// Renders tool chips, plan checklist, task progress and confirm cards from the
// fields added by applyAgentEvent(): _tools, _plan, _tasks, _confirms.
import React from "react";

const chipColor = (st) => st === "ok" ? "#10b981" : st === "error" ? "#ef4444" : "#fbbf24";
const shortName = (n) => String(n || "tool").replace(/^ui_/, "");

export function AgentToolChips({ tools }) {
  if (!tools || !tools.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
      {tools.map((t, i) => (
        <span key={t.call_id || i} data-testid="tool-chip" data-tool={t.name} data-status={t.status}
          title={t.summary || t.name}
          style={{ fontSize: 10, padding: "1px 6px", borderRadius: 99, border: `1px solid ${chipColor(t.status)}88`,
            color: chipColor(t.status), background: `${chipColor(t.status)}14`, whiteSpace: "nowrap" }}>
          🔧 {shortName(t.name)} {t.status === "ok" ? "✓" : t.status === "error" ? "✗" : "…"}{t.ms != null ? ` ${Math.round(t.ms)}ms` : ""}
        </span>
      ))}
    </div>
  );
}

export function AgentPlan({ plan }) {
  if (!plan || !plan.length) return null;
  const icon = (s) => s === "done" || s === "ok" || s === "completed" ? "☑" : s === "running" || s === "in_progress" ? "▶" : s === "failed" || s === "error" ? "✗" : "☐";
  return (
    <div data-testid="agent-plan" style={{ marginTop: 6, padding: "6px 8px", border: "1px solid #1e3a5f", borderRadius: 6, fontSize: 11 }}>
      <div style={{ fontSize: 9, letterSpacing: 1, color: "#94a3b8", marginBottom: 3 }}>PLAN</div>
      {plan.map((s, i) => (
        <div key={s.id || i} style={{ opacity: s.status === "done" || s.status === "ok" ? 0.7 : 1 }}>
          {icon(s.status)} {s.title || s.text || String(s)}
        </div>
      ))}
    </div>
  );
}

export function AgentTasks({ tasks }) {
  const entries = Object.entries(tasks || {});
  if (!entries.length) return null;
  return (
    <div style={{ marginTop: 6, fontSize: 10, color: "#94a3b8" }}>
      {entries.map(([id, t]) => <div key={id}>⚙ {t.status || "running"}{t.message ? ` — ${t.message}` : ""}</div>)}
    </div>
  );
}

export function AgentConfirmCards({ confirms, onConfirm, accent = "#10b981" }) {
  if (!confirms || !confirms.length) return null;
  return (
    <>
      {confirms.map(c => (
        <div key={c.call_id} data-testid="confirm-card" data-status={c.status}
          style={{ marginTop: 8, padding: "8px 10px", border: `1px solid ${accent}88`, borderRadius: 8, background: "#0b1626" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: accent, letterSpacing: 0.5 }}>🔒 CONFIRM · {shortName(c.name)}</div>
          <div style={{ fontSize: 12, marginTop: 3, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{c.summary || JSON.stringify(c.input || {}).slice(0, 200)}</div>
          {c.status === "pending" || c.status === "sending" ? (
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button data-testid="confirm-approve" disabled={c.status === "sending"} onClick={() => onConfirm?.(c.call_id, true)}
                style={{ background: accent + "22", border: `1px solid ${accent}`, color: accent, padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>✅ Confirm</button>
              <button data-testid="confirm-cancel" disabled={c.status === "sending"} onClick={() => onConfirm?.(c.call_id, false)}
                style={{ background: "#ef444422", border: "1px solid #ef4444", color: "#ef4444", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>❌ Cancel</button>
            </div>
          ) : (
            <div style={{ fontSize: 11, marginTop: 4, color: c.status === "approved" ? accent : c.status === "error" ? "#ef4444" : "#94a3b8" }}>
              {c.status === "approved" ? "✅ Confirmed" : c.status === "cancelled" ? "Cancelled" : "❌ Failed"}{c.result ? ` — ${c.result}` : ""}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

export function AgentMessageExtras({ m, onConfirm, accent }) {
  if (!m || !m._agent) return null;
  return (
    <>
      <AgentPlan plan={m._plan} />
      <AgentToolChips tools={m._tools} />
      <AgentTasks tasks={m._tasks} />
      <AgentConfirmCards confirms={m._confirms} onConfirm={onConfirm} accent={accent} />
    </>
  );
}
